import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/ui';

import styles from './WorkweekPicker.module.css';
import { DEFAULT_WORKDAYS, sameDays } from './workdays';

/**
 * Seven day buttons (Monday first) choosing the days of the Calendar's "Work week" view. Any
 * combination is allowed, but at least one day stays selected. A display preference of the
 * board: it never touches dates, deadlines or recurrence rules.
 */
export function WorkweekPicker({
  value,
  onChange,
  locale,
  disabled = false,
}: {
  /** Selected ISO weekdays, ascending. */
  value: readonly number[];
  onChange: (days: number[]) => void;
  locale: string;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const id = useId();
  // 2026-10-05 is a Monday: its formatting gives the localized abbreviations in ISO order.
  const short = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' });
  const long = new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone: 'UTC' });
  const date = (day: number) => new Date(Date.UTC(2026, 9, 4 + day));
  const toggle = (day: number) => {
    const next = value.includes(day) ? value.filter((d) => d !== day) : [...value, day];
    if (next.length === 0) return; // the last working day cannot be removed
    onChange(next.toSorted((a, b) => a - b));
  };
  return (
    <fieldset className={styles.root} disabled={disabled} data-testid="workweek-picker">
      <legend id={`${id}-label`}>{t('boards.workweek.label')}</legend>
      <div className={styles.days} role="group" aria-labelledby={`${id}-label`}>
        {[1, 2, 3, 4, 5, 6, 7].map((day) => {
          const selected = value.includes(day);
          return (
            <button
              key={day}
              type="button"
              className={styles.day}
              aria-pressed={selected}
              aria-label={long.format(date(day))}
              title={long.format(date(day))}
              data-weekend={day > 5 ? 'true' : undefined}
              onClick={() => {
                toggle(day);
              }}
            >
              {short.format(date(day))}
            </button>
          );
        })}
      </div>
      <div className={styles.foot}>
        <small id={`${id}-hint`}>
          {value.length === 1 ? t('boards.workweek.oneDay') : t('boards.workweek.hint')}
        </small>
        {!sameDays(value, DEFAULT_WORKDAYS) && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => {
              onChange([...DEFAULT_WORKDAYS]);
            }}
          >
            {t('boards.workweek.reset')}
          </Button>
        )}
      </div>
    </fieldset>
  );
}
